import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchSlots,
  createBooking,
  ApiError,
  type BusinessInfo,
  type ServiceInfo,
  type Slot,
} from "../lib/api.ts";
import { BusinessHeader } from "../components/BusinessHeader.tsx";
import { ServiceList } from "../components/ServiceList.tsx";
import { DatePicker } from "../components/DatePicker.tsx";
import { SlotGrid } from "../components/SlotGrid.tsx";
import { CustomerForm, type CustomerFormData } from "../components/CustomerForm.tsx";
import { ConfirmSummary } from "../components/ConfirmSummary.tsx";

// ─── StaffPicker without "Any available" ─────────────────────────────────────

interface StaffInfo {
  id: string;
  name: string;
  avatar_url?: string | null;
}

interface StaffCardProps {
  member: StaffInfo;
  selected: boolean;
  onSelect: () => void;
}

function StaffCard({ member, selected, onSelect }: StaffCardProps) {
  const initial = member.name.charAt(0).toUpperCase();
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`shrink-0 flex flex-col items-center gap-1.5 px-3 py-2 rounded-[var(--radius)] border-2 transition-all duration-150 min-w-[72px] ${
        selected
          ? "border-[var(--accent)] bg-[var(--accent)]/10"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      {member.avatar_url ? (
        <img
          src={member.avatar_url}
          alt={member.name}
          className="w-12 h-12 rounded-full object-cover"
        />
      ) : (
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold text-white"
          style={{ backgroundColor: selected ? "var(--accent)" : "#9ca3af" }}
        >
          {initial}
        </div>
      )}
      <span
        className={`text-xs font-medium leading-tight text-center max-w-[80px] truncate ${
          selected ? "text-[var(--accent)]" : "text-gray-700"
        }`}
      >
        {member.name}
      </span>
    </button>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  slug: string;
  business: BusinessInfo;
  services: ServiceInfo[];
  staff: Array<{ id: string; name: string; avatar_url?: string | null }>;
}

type Step = "provider" | "services" | "schedule_details" | "confirm";

// ─── Component ───────────────────────────────────────────────────────────────

export function ProviderFlow({ slug, business, services, staff }: Props) {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("provider");
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);

  const today = new Date().toISOString().split("T")[0] ?? "";
  const [date, setDate] = useState<string>(today);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const [customerData, setCustomerData] = useState<CustomerFormData | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Fetch slots when date or services or staff changes on schedule_details step
  useEffect(() => {
    if (step !== "schedule_details" || selectedServiceIds.length === 0) return;
    setSlotsLoading(true);
    setSlotsError(null);
    setSelectedSlot(null);
    fetchSlots(slug, date, selectedServiceIds, selectedStaffId ?? undefined)
      .then(setSlots)
      .catch(() => setSlotsError("Failed to load available slots."))
      .finally(() => setSlotsLoading(false));
  }, [step, date, selectedServiceIds, selectedStaffId, slug]);

  function toggleService(id: string) {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }

  function handleCustomerSubmit(data: CustomerFormData) {
    setCustomerData(data);
    setStep("confirm");
  }

  async function handleConfirm() {
    if (!selectedSlot || !customerData) return;
    setBookingLoading(true);
    setBookingError(null);
    try {
      const confirmation = await createBooking(slug, {
        service_ids: selectedServiceIds,
        staff_id: selectedStaffId ?? undefined,
        date: date,
        start_time: selectedSlot.start,
        meeting_mode: primaryMode,
        customer: {
          name: customerData.name,
          email: customerData.email,
          phone: customerData.phone,
          notes: customerData.notes || undefined,
        },
      });
      if (confirmation.checkout_url) {
        window.location.href = confirmation.checkout_url;
        return;
      }

      const serviceNames = selectedServices.map((s) => s.name).join(", ");
      const params = new URLSearchParams({
        token: confirmation.cancel_token,
        id: confirmation.appointment_id,
        date: date,
        start_time: selectedSlot.start,
        service: serviceNames,
        staff: selectedStaffMember?.name ?? "",
        meeting_mode: primaryMode,
      });
      navigate(`/${slug}/success?${params.toString()}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setBookingError("That time was just taken. Please pick another slot.");
        setStep("schedule_details");
        setSelectedSlot(null);
      } else {
        setBookingError("Booking failed. Please try again.");
      }
    } finally {
      setBookingLoading(false);
    }
  }

  // Derived data for confirm summary
  const selectedServices = services.filter((s) => selectedServiceIds.includes(s.id));
  const selectedStaffMember = staff.find((m) => m.id === selectedStaffId) ?? null;
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration_min, 0);
  const totalPrice = selectedServices.reduce((sum, s) => sum + (s.price_cents ?? 0), 0);
  const primaryCurrency = selectedServices[0]?.currency ?? "USD";
  const primaryMode = selectedServices[0]?.meeting_mode ?? "in_person";

  const meetingModes = [...new Set(services.map((s) => s.meeting_mode))];

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "var(--font)" }}>
      <BusinessHeader business={business} meetingModes={meetingModes} />

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">

        {/* ── Step 1: Pick Provider ── */}
        {step === "provider" && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Choose your provider</h2>
              <p className="text-sm text-gray-500 mt-1">Select who you'd like to book with.</p>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {staff.map((member) => (
                <StaffCard
                  key={member.id}
                  member={member}
                  selected={selectedStaffId === member.id}
                  onSelect={() => setSelectedStaffId(member.id)}
                />
              ))}
            </div>
            <button
              type="button"
              disabled={!selectedStaffId}
              onClick={() => setStep("services")}
              className="w-full py-3 px-4 rounded-[var(--radius)] text-white text-sm font-semibold disabled:opacity-40 transition-opacity hover:opacity-90"
              style={{ background: "var(--accent)", borderRadius: "var(--radius)" }}
            >
              Continue
            </button>
          </div>
        )}

        {/* ── Step 2: Pick Services ── */}
        {step === "services" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStep("provider")}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                &larr; Back
              </button>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Select services</h2>
                {selectedStaffMember && (
                  <p className="text-sm text-gray-500">with {selectedStaffMember.name}</p>
                )}
              </div>
            </div>
            <ServiceList
              services={services}
              selected={selectedServiceIds}
              onToggle={toggleService}
            />
            <button
              type="button"
              disabled={selectedServiceIds.length === 0}
              onClick={() => setStep("schedule_details")}
              className="w-full py-3 px-4 rounded-[var(--radius)] text-white text-sm font-semibold disabled:opacity-40 transition-opacity hover:opacity-90"
              style={{ background: "var(--accent)", borderRadius: "var(--radius)" }}
            >
              Continue
            </button>
          </div>
        )}

        {/* ── Step 3: Schedule + Details ── */}
        {step === "schedule_details" && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStep("services")}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                &larr; Back
              </button>
              <h2 className="text-lg font-semibold text-gray-900">Choose a date &amp; time</h2>
            </div>

            <DatePicker selected={date} onSelect={(d) => { setDate(d); setSelectedSlot(null); }} />

            <SlotGrid
              slots={slots}
              selected={selectedSlot?.start ?? null}
              onSelect={setSelectedSlot}
              loading={slotsLoading}
              error={slotsError}
            />

            {selectedSlot && (
              <div className="pt-2">
                <p className="text-sm font-medium text-gray-700 mb-3">Your details</p>
                <CustomerForm
                  onSubmit={handleCustomerSubmit}
                  onBack={() => setSelectedSlot(null)}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Step 4: Confirm ── */}
        {step === "confirm" && selectedSlot && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Review your booking</h2>
            <ConfirmSummary
              serviceNames={selectedServices.map((s) => s.name)}
              staffName={selectedStaffMember?.name ?? null}
              date={date}
              time={selectedSlot.start}
              durationMin={totalDuration}
              priceCents={totalPrice > 0 ? totalPrice : null}
              currency={primaryCurrency}
              meetingMode={primaryMode}
              onConfirm={handleConfirm}
              onBack={() => setStep("schedule_details")}
              loading={bookingLoading}
              error={bookingError}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-xs text-gray-400">
        Powered by{" "}
        <a
          href="https://zedule.app"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium hover:text-gray-600 transition-colors"
        >
          Zedule
        </a>
      </div>
    </div>
  );
}
